import { FormEvent, useEffect, useState } from 'react';
import { IBaseResponse, ICenterBase, IVoucherHolderForList } from '@nkr/models/index';
import { Alert, Button, Col, ControlLabel, Form, FormControl, FormGroup, HelpBlock, Label, Modal, ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import { IRecipientByCenter } from '@nkr/store/index';
import usePrevious from '@nkr/services/use-previous';

interface IRecipientOption extends IRecipientByCenter {
    label: string
}

interface IProps {
    show: boolean,
    data: IVoucherHolderForList,
    centerList: ICenterBase[],
    recipientList: IRecipientByCenter[],
    onActivateVoucher: (voucherHolderId: number, centerId: number, alias: string, existingRecipientId: number, isNewRecipient: boolean) => Promise<IBaseResponse>
    onCenterChanged: (centerId: number) => void,
    onComplete: () => void,
    onCancel: () => void
}

const ActivateVoucher = (props: IProps) => {
    const [center, setCenter] = useState(0);
    const [activationType, setActivationType] = useState(1);
    const [existingRecipient, setExistingRecipient] = useState<IRecipientOption[]>([]);
    const [alias, setAlias] = useState('');
    const [error, setError] = useState('');

    const previousShow = usePrevious(props.show);

    useEffect(() => {
        if (previousShow == false && props.show) {
            const recipientIdGenerated = props.data?.recipientIdGenerated ?? 0;
            setCenter(props.data != null ? props.data.donorCenterId : 0);
            setActivationType(recipientIdGenerated > 0 ? 2 : 1);
            setAlias('');
            setError('');

            if (recipientIdGenerated > 0) {
                const match = props.recipientList.find(r => r.id === recipientIdGenerated);
                if (match) {
                    const label = match.firstName !== '' && match.lastName !== ''
                        ? `${match.firstName} ${match.lastName}, Alias: ${match.alias}, DOB: ${match.birthDate}`
                        : `Alias: ${match.alias}, DOB: ${match.birthDate}`;
                    setExistingRecipient([{ ...match, label }]);
                } else {
                    setExistingRecipient([]);
                }
            } else {
                setExistingRecipient([]);
            }
        }
    }, [props.show, props.data]);

    const handleCenterChange = (e: FormEvent<FormControl & HTMLSelectElement>) => {
        let centerId = parseInt(e.currentTarget.value);

        setCenter(centerId);
        props.onCenterChanged(centerId);
    };

    const handleActivationTypeChange = (e) => {
        setActivationType(e);
        setAlias('');
        setExistingRecipient([]);
    }

    const handleAliasChange = (e: FormEvent<FormControl & HTMLInputElement>) => {
        setAlias(e.currentTarget.value);
    };

    const handleExistingChange = (existingRecipient: IRecipientOption[]) => {
        setExistingRecipient(existingRecipient);
    };

    const handleActivate = async () => {
        let existingRecipientId = existingRecipient.length > 0 ? existingRecipient[0].id : 0;

        props.onActivateVoucher(props.data.id, center, alias, existingRecipientId, activationType == 1)
            .then(response => {
                if (response.data.success) {
                    props.onComplete();
                }
            })
            .catch(error => {
                setError(error.response.data);
            });
    };

    let firstName = props.data != null ? props.data.firstName : '';
    let lastName = props.data != null ? props.data.lastName : '';

    const recipientIdGenerated = props.data?.recipientIdGenerated ?? 0;
    let recipientList = props.recipientList
        .filter(r => recipientIdGenerated > 0 ? r.id === recipientIdGenerated : true)
        .map<IRecipientOption>(recipient => {
            let label = recipient.firstName != '' && recipient.lastName != '' ? `${recipient.firstName} ${recipient.lastName}, Alias: ${recipient.alias}, DOB: ${recipient.birthDate}` : `Alias: ${recipient.alias}, DOB: ${recipient.birthDate}`;
            return { ...recipient, label };
        });

    let disable = activationType == 1 ? center == 0 || alias == '' : center == 0 || existingRecipient.length == 0;

    return (
        <Modal backdrop='static' show={props.show} onHide={props.onCancel}>
            <Modal.Header closeButton>
                Activate Voucher for <strong>{firstName} {lastName}</strong>
            </Modal.Header>
            <Modal.Body style={{ padding: 0 }}>
                <div style={{ padding: 15 }}>
                    {error != '' && (
                        <Alert bsStyle='danger' bsSize='sm' style={{ textAlign: 'center' }}>
                            ATTENTION! {error}
                        </Alert>
                    )}

                    <div>
                        <div style={{ marginBottom: 5 }}>To activate the following voucher, please specify the recipient center and alias below...</div>
                        <blockquote>{firstName} {lastName}</blockquote>
                    </div>

                    <Form horizontal>
                        <FormGroup>
                            <Col xs={12} lg={3} componentClass={ControlLabel}>Activate As:</Col>
                            <Col xs={12} lg={9}>
                                <ToggleButtonGroup type='radio' name='activationType' defaultValue={activationType} onChange={handleActivationTypeChange}>
                                    <ToggleButton value={1}>New Recipient</ToggleButton>
                                    <ToggleButton value={2}>Existing Recipient</ToggleButton>
                                </ToggleButtonGroup>
                            </Col>
                        </FormGroup>

                        <FormGroup>
                            <Col xs={12} lg={3} componentClass={ControlLabel}>Recipient Center:</Col>
                            <Col xs={12} lg={6}>
                                <FormControl
                                    componentClass='select'
                                    value={center}
                                    onChange={handleCenterChange}
                                >
                                    <option value={0}>- Select -</option>
                                    {props.centerList.map(item =>
                                        <option key={'center-' + item.id} value={item.id}>{item.alias}</option>
                                    )}
                                </FormControl>
                            </Col>
                        </FormGroup>

                        {activationType == 1 ?
                            <FormGroup>
                                <Col xs={12} lg={3} componentClass={ControlLabel}>Recipient Alias:</Col>
                                <Col xs={12} lg={6}>
                                    <FormControl
                                        placeholder='e.g. ABC123'
                                        value={alias}
                                        onChange={handleAliasChange} />
                                </Col>
                            </FormGroup>
                            :
                            <FormGroup>
                                <Col xs={12} lg={3} componentClass={ControlLabel}>Existing Recipient:</Col>
                                <Col xs={12} lg={9}>
                                    <Typeahead
                                        id='nkr-alias'
                                        clearButton={true}
                                        labelKey='label'
                                        options={recipientList}
                                        selected={existingRecipient}
                                        // disabled={isNotNkrTransplant}
                                        filterBy={['alias', 'firstName', 'lastName']}
                                        renderInput={(inputProps) => (
                                            <FormControl {...inputProps} placeholder='Click here...' />
                                        )}
                                        renderMenuItemChildren={(option, props, index) => {
                                            return (
                                                <div style={{ borderBottom: '1px solid #ddd', paddingBottom: 5 }}>
                                                    <div>Alias: {option.alias}</div>
                                                    {option.firstName != '' && option.lastName != '' && (
                                                        <div><small>Name: {option.firstName} {option.lastName}</small></div>
                                                    )}
                                                    <div><small>DOB: {option.birthDate}</small></div>
                                                    {/* <div><small>Transplanted: {option.transplantDate}</small></div> */}
                                                </div>
                                            );
                                        }}
                                        onChange={handleExistingChange}
                                    />
                                </Col>
                            </FormGroup>
                        }
                    </Form>
                </div>
                <div className='bg-warning' style={{ padding: 3, textAlign: 'center' }}>WARNING! Once you activate a voucher, all other vouchers will be retired</div>
            </Modal.Body>
            <Modal.Footer>
                <Button bsStyle='primary' disabled={disable} onClick={handleActivate}>Activate</Button>
                <Button onClick={props.onCancel}>Cancel</Button>
            </Modal.Footer>
        </Modal>
    );
}

export default ActivateVoucher;